import React from 'react';
import {
  Box, Paper, Stack, Typography, IconButton, Tooltip, Grid, Skeleton, Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Sparkline from '../Sparkline';
import { SHADOWS, V2 } from '../../theme/tokens'
import { formatInt, formatMoney } from '../../utils/format'

const DONUT_PALETTE_LIGHT = [V2.infoText, V2.agent, V2.okText, V2.warnText, '#607d8b'];
const DONUT_PALETTE_DARK  = [V2.info100, '#ce93d8', '#a5d6a7', '#ffcc80', '#b0bec5'];

/** Utilities */
// 🔴 Bis 2026-10-06 `Intl.NumberFormat('en-GB')` → `€1,234.56`, englisch neben
// den deutschen Zahlen derselben Karte (Regel-16-Gate B1). Jetzt die Hausform.
export const eur = (n) => formatMoney(n);

const CARD_SX = {
  p: 1.5,
  borderRadius: 3,
  borderColor: 'divider',
  boxShadow: SHADOWS.panel,
};

/** Section container (Paper on grey bg) */
export function DashboardSection({ title, actions = null, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, ...sx }}>
      {(title || actions) && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {title && <Typography variant="subtitle1">{title}</Typography>}
          <Box sx={{ flex: 1 }} />
          {actions}
        </Stack>
      )}
      {children}
    </Paper>
  );
}

/** Grid helpers */
export function DashboardGrid({ spacing = 2, children }) {
  return <Grid container spacing={spacing}>{children}</Grid>;
}
export function Col({ xs = 12, sm = 6, md = 3, children }) {
  return <Grid size={{ xs: xs, sm: sm, md: md }}>{children}</Grid>;
}

/** Single KPI tile */
export function KpiTile({ label, value, hint, loading = false, valueVariant = 'h6', after = null }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          {hint && (
            <Tooltip title={hint} arrow>
              <IconButton size="small"><InfoOutlined fontSize="inherit" /></IconButton>
            </Tooltip>
          )}
        </Stack>
        {loading ? (
          <Skeleton variant="text" width={140} height={28} />
        ) : (
          <Typography variant={valueVariant}>{value}</Typography>
        )}
        {after}
      </Stack>
    </Paper>
  );
}

/** Sparkline card (uses your Sparkline.jsx) */
export function SparkCard({ label, data = [], loading = false }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
      {loading ? (
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, width: 180, mx: 'auto' }} />
      ) : (
        <Sparkline data={data} width={180} height={40} />
      )}
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

/** Minimal Donut (SVG) to avoid extra deps */
export function DonutCard({ label, items = [], loading = false, palette, bare = false, sx = {} }) {
  // items: [{ name, value }]
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const defaultPalette = isDark ? DONUT_PALETTE_DARK : DONUT_PALETTE_LIGHT;
  const trackColor = isDark ? '#333' : V2.wash;
  const textFill = theme.palette.text.secondary;

  const total = items.reduce((s, x) => s + (x.value || 0), 0);
  const radius = 56, thickness = 18, cx = 70, cy = 70, C = 2 * Math.PI * radius;
  const [hover, setHover] = React.useState(null);
  let acc = 0;
  const arcs = items.map((it, i) => {
    const frac = total > 0 ? (it.value || 0) / total : 0;
    const len = frac * C;
    const dasharray = `${len} ${C - len}`;
    const dashoffset = -acc;
    acc += len;
    const color = palette?.[i % (palette.length || 1)] || defaultPalette[i % 5];
    return {
      dasharray,
      dashoffset,
      color,
      name: it.name,
      value: it.value,
      label: it.label,
      meta: it.meta,
      key: i,
    };
  });

  const Wrapper = bare ? Box : Paper;
  const wrapperSx = bare ? {} : { ...CARD_SX, ...sx };
  return (
    <Wrapper sx={wrapperSx}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
        {loading ? (
          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1, width: 140 }} />
        ) : (
          <svg width="140" height="140" viewBox="0 0 140 140" role="img">
            <circle cx={cx} cy={cy} r={radius} fill="none" stroke={trackColor} strokeWidth={thickness} />
            {arcs.map((a, idx) => {
              const slice = items[idx] || {};
              const displayLabel = slice.label || slice.name || '';
              const detail = slice.meta || slice.subtitle;
              // 🔴 Bis 2026-10-06 `slice.value?.toLocaleString?.() || 0` — die
              // Browser-Locale. Im englischen Browser zeigte der Tooltip `1,234`,
              // waehrend Legende und Mitte DESSELBEN Donuts `1.234` schrieben
              // (R16-W1). `formatInt` ist die Hausform; unbekannt wird `—`, nicht
              // die behauptete Null (Regel 22 §3).
              const valueLabel = formatInt(slice.value);
              const tooltip = detail ? `${displayLabel}: ${valueLabel} (${detail})` : `${displayLabel}: ${valueLabel}`;
              return (
                <Tooltip key={a.key} title={tooltip} arrow>
                  <circle
                    cx={cx}
                    cy={cy}
                    r={radius}
                    fill="none"
                    stroke={a.color}
                    strokeWidth={hover === idx ? thickness + 2 : thickness}
                    strokeDasharray={a.dasharray}
                    strokeDashoffset={a.dashoffset}
                    transform={`rotate(-90 ${cx} ${cy})`}
                    onMouseEnter={() => setHover(idx)}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: 'pointer', transition: 'stroke-width 0.2s ease' }}
                  />
                </Tooltip>
              );
            })}
            <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize="12" fill={textFill}>
              {hover != null ? (
                <>
                  <tspan x="50%" dy="-0.6em">
                    {items[hover]?.label || items[hover]?.name || ''}
                  </tspan>
                  {items[hover]?.meta && (
                    <tspan x="50%" dy="1.2em">
                      {items[hover]?.meta}
                    </tspan>
                  )}
                  <tspan x="50%" dy="1.2em">
                    {formatInt(items[hover]?.value ?? 0)}
                  </tspan>
                </>
              ) : (
                total > 0 ? formatInt(total) : '—'
              )}
            </text>
          </svg>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {(items || []).filter(it => (it?.value || 0) > 0).slice(0, 8).map((it, i) => (
            <Chip
              key={`${it.name}-${i}`}
              size="small"
              label={`${it.name}: ${formatInt(it.value)}`}
              sx={{ bgcolor: (theme) => theme.palette.action.hover }}
            />
          ))}
        </Box>
      </Box>
    </Wrapper>
  );
}
